import { Box, Flex, IconButton, Separator, Stack, Text } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import sortBy from 'lodash/sortBy'
import type { FC } from 'react'
import { memo } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PiArrowDownDuotone, PiSpinnerBall } from 'react-icons/pi'

import useGetObject from '@app/hooks/useGetObject'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import ChatDrawerHeader from '@app/pages/strategies/components/aiChat/components/chatDrawerHeader'
import LoadingThrobber from '@app/pages/strategies/components/aiChat/components/loadingThrobber'
import MessageGroup from '@app/pages/strategies/components/aiChat/components/messageGroup'
import PromptForm from '@app/pages/strategies/components/aiChat/components/promptForm'
import { useChatNavigationContext } from '@app/pages/strategies/components/aiChat/contexts/aiChatNavigationContext'
import type { DomainMessage } from '@app/types'
import { ChatProcessedStateEnum } from '@graphql/types'
import type { AiAssistantRoleEnum, PipelineEnum, ChattableObjectInput } from '@graphql/types'

type Props = {
  chatId: string
  chattableType: keyof ChattableObjectInput
  chattableId: string
  strategyId?: string
  inDrawer?: boolean
}

type MessageGrouping = [
  string, // groupKey
  AiAssistantRoleEnum, // role
  Pick<DomainMessage, 'id' | 'classType' | 'content' | 'role' | 'userId' | 'createdAt'>[]
]

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const Show: FC<Props> = ({ chattableType, chattableId, chatId, strategyId = null, inDrawer = true }) => {
  const storeMessages = useGetObjectsByProperties('message', { chatId })
  const chat = useGetObject(chatId, 'chat')
  const [autoScroll, setAutoScroll] = useState(true)
  const realTimeStatus = chat?.realTimeStatus
  const pipelineId = chat?.pipelineSlug?.replace(/-/g, '_') as PipelineEnum
  const navContext = useChatNavigationContext()

  const messages = useMemo(
    () =>
      sortBy(storeMessages, (message) => new Date(message.createdAt).getTime()).filter((message) => message.content),
    [storeMessages]
  )

  const scrollRef = useRef(null)

  // condense consecutive messages from the same user
  const messageGroups = useMemo(
    () =>
      messages.reduce<MessageGrouping[]>((acc, message) => {
        const [lastKey, lastRole, lastMessages] = acc[acc.length - 1] || []

        if (
          lastMessages &&
          ((lastRole === 'user' && message.role === 'user') ||
            (lastRole === 'assistant' && message.role === 'assistant'))
        ) {
          acc[acc.length - 1] = [lastKey, lastRole, [...lastMessages, message]]
        } else {
          acc.push([message.id, message.role, [message]])
        }

        return acc
      }, []),
    [messages]
  )

  const onConfirmDelete = useCallback(() => {
    navContext.list()
  }, [navContext])

  useEffect(() => {
    const el = scrollRef?.current

    const detectScrollBottom = () => {
      if (el) {
        setAutoScroll(el.scrollTop + el.clientHeight >= el.scrollHeight - 100)
      }
    }

    el?.addEventListener('scroll', detectScrollBottom)

    return () => {
      el?.removeEventListener('scroll', detectScrollBottom)
    }
  }, [])

  const scrollToBottom = () => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    }
  }

  // scroll to the bottom of the list when the messages list changes
  useEffect(() => {
    if (scrollRef.current && autoScroll) {
      scrollToBottom()

      setTimeout(() => {
        scrollToBottom()
      }, 1000)
    }
  }, [autoScroll, scrollRef?.current?.scrollHeight])

  const showLoader = useMemo(
    () => storeMessages?.length === 1 && chat.processedState === ChatProcessedStateEnum.Processing,
    [storeMessages, chat]
  )

  return (
    <Flex pos="relative" direction="column" overflow="hidden" h="100%" bg="bg">
      <ChatDrawerHeader chatId={chatId} onConfirmDelete={onConfirmDelete} closable={inDrawer} />
      {!autoScroll && (
        <Box position="absolute" left="50%" bottom={48}>
          <IconButton rounded="full" onClick={scrollToBottom} size="sm" colorPalette="gray" variant="surface">
            <PiArrowDownDuotone />
          </IconButton>
        </Box>
      )}
      <Flex ref={scrollRef} overflowY="auto" pt="0" pb="40" grow={1}>
        <Stack
          gap="10"
          w="100%"
          mx="auto"
          px={4}
          separator={
            <Box ml="14!">
              <Separator />
            </Box>
          }
        >
          {messageGroups?.map(([groupKey, role, contents]) => (
            <MessageGroup
              key={groupKey}
              role={role}
              chat={chat}
              messages={contents.map((message) => [message.id, message])}
            />
          ))}
          {showLoader && (
            <Box ml={8}>
              <LoadingThrobber size="sm" />
            </Box>
          )}
          {realTimeStatus && (
            <Text ml={8} fontSize="md" display="flex" alignItems="center" gap={2}>
              <Box as="span" display="inline-block" animation={`${spin} 1.5s linear infinite`}>
                <PiSpinnerBall />
              </Box>
              {realTimeStatus}
            </Text>
          )}
        </Stack>
      </Flex>

      <Flex mx="4" py="4" bgGradient="linear(to-t, bg.canvas 80%, rgba(0,0,0,0))" grow={0} insetX="0">
        <Stack gap={4} w="100%">
          <PromptForm
            pipelineId={pipelineId}
            chatId={chatId}
            chattableType={chattableType}
            chattableId={chattableId}
            strategyId={strategyId}
          />
        </Stack>
      </Flex>
    </Flex>
  )
}

export default memo(Show)
